import { normalizePrice } from './format'

/**
 * Tyre offers — the products a customer chooses between once the size is known.
 *
 * The shape is deliberately the contract a supplier feed fills rather than a
 * mirror of any one supplier's payload. Everything except brand, model and
 * price is optional, because feeds vary and the screen has to degrade to
 * "brand, model, price" without looking broken.
 *
 * Note on the EU label: it was re-scaled in 2021, so grades run A–E rather
 * than the older A–G. Both are accepted here since older stock still carries
 * the previous grading.
 */

export type TyreTier = 'budget' | 'mid' | 'premium'
export type TyreSeason = 'summer' | 'winter' | 'all-season'

/** EU label grade. Uppercase single letter; A is best. */
export type EuGrade = string

export interface TyreOffer {
  /** Supplier's stable identifier for this product at this size. */
  id: string
  brand: string
  model: string
  size: string

  /** Price for one tyre, fitted, including VAT. */
  fullyFittedPrice: number

  tier?: TyreTier | null
  season?: TyreSeason | null

  /** EU label: fuel efficiency and wet grip grades, and exterior noise in dB. */
  fuelEfficiency?: EuGrade | null
  wetGrip?: EuGrade | null
  noiseDb?: number | null

  loadIndex?: string | null
  speedRating?: string | null

  runFlat?: boolean
  extraLoad?: boolean

  /** Null when the supplier does not report a count. */
  stockQuantity?: number | null
  /** Free text such as "Next day" — shown as-is. */
  availability?: string | null

  /** Which axle this offer is for, when the request was for a staggered car. */
  axle?: TyreAxle | null

  /** How many tyres the server priced `totalPrice` for: the quantity asked for. */
  quantity?: number | null
  /**
   * What the customer pays for `quantity` of this tyre, fitted, VAT included:
   * the booking's tyre line. Can differ by a penny or two from the per-tyre
   * price times the quantity once VAT is added, so it is the figure to show.
   */
  totalPrice?: number | null
}

export type TyreAxle = 'FRONT' | 'REAR'

function isAxle(value: unknown): value is TyreAxle {
  return value === 'FRONT' || value === 'REAR'
}

export interface TyreOfferFilters {
  brands: string[]
  tiers: TyreTier[]
  seasons: TyreSeason[]
}

export const EMPTY_FILTERS: TyreOfferFilters = { brands: [], tiers: [], seasons: [] }

export const TIER_LABELS: Record<TyreTier, string> = {
  budget: 'Budget',
  mid: 'Mid-range',
  premium: 'Premium',
}

export const SEASON_LABELS: Record<TyreSeason, string> = {
  summer: 'Summer',
  winter: 'Winter',
  'all-season': 'All season',
}

/** Order tiers cheapest-first so the filter row reads like a price ladder. */
const TIER_ORDER: TyreTier[] = ['budget', 'mid', 'premium']
const SEASON_ORDER: TyreSeason[] = ['summer', 'winter', 'all-season']

function isTier(value: unknown): value is TyreTier {
  return value === 'budget' || value === 'mid' || value === 'premium'
}

function isSeason(value: unknown): value is TyreSeason {
  return value === 'summer' || value === 'winter' || value === 'all-season'
}

/**
 * Normalises a supplier payload into offers we can render.
 *
 * Anything without a brand, model or usable price is dropped rather than shown
 * as a half-empty card — an unpriced tyre is not something a customer can buy.
 */
export function normalizeOffers(raw: unknown[]): TyreOffer[] {
  if (!Array.isArray(raw)) return []

  return raw
    .map((item): TyreOffer | null => {
      if (!item || typeof item !== 'object') return null
      const record = item as Record<string, unknown>

      const brand = typeof record.brand === 'string' ? record.brand.trim() : ''
      const model = typeof record.model === 'string' ? record.model.trim() : ''
      const price = normalizePrice(record.fullyFittedPrice)

      if (!brand || !model || price == null || price <= 0) return null

      return {
        id: String(record.id ?? `${brand}-${model}-${price}`),
        brand,
        model,
        size: typeof record.size === 'string' ? record.size : '',
        fullyFittedPrice: price,
        tier: isTier(record.tier) ? record.tier : null,
        season: isSeason(record.season) ? record.season : null,
        fuelEfficiency: normalizeGrade(record.fuelEfficiency),
        wetGrip: normalizeGrade(record.wetGrip),
        noiseDb: normalizePrice(record.noiseDb),
        loadIndex: typeof record.loadIndex === 'string' ? record.loadIndex : null,
        speedRating: typeof record.speedRating === 'string' ? record.speedRating : null,
        runFlat: record.runFlat === true,
        extraLoad: record.extraLoad === true,
        stockQuantity:
          typeof record.stockQuantity === 'number' && Number.isFinite(record.stockQuantity)
            ? record.stockQuantity
            : null,
        availability: typeof record.availability === 'string' ? record.availability : null,
        axle: isAxle(record.axle) ? record.axle : null,
        quantity:
          typeof record.quantity === 'number' && Number.isInteger(record.quantity)
            ? record.quantity
            : null,
        totalPrice: normalizePrice(record.totalPrice),
      }
    })
    .filter((offer): offer is TyreOffer => offer !== null)
}

function normalizeGrade(value: unknown): EuGrade | null {
  if (typeof value !== 'string') return null
  const grade = value.trim().toUpperCase()
  return /^[A-G]$/.test(grade) ? grade : null
}

/**
 * Cheapest first, which is what customers scan for.
 *
 * Ties break on brand then model so the order is stable between renders —
 * supplier feeds routinely return several identically-priced products, and a
 * list that reshuffles as you filter feels broken.
 */
export function sortOffersByPrice(offers: TyreOffer[]): TyreOffer[] {
  return [...offers].sort((a, b) => {
    if (a.fullyFittedPrice !== b.fullyFittedPrice) return a.fullyFittedPrice - b.fullyFittedPrice
    if (a.brand !== b.brand) return a.brand.localeCompare(b.brand)
    return a.model.localeCompare(b.model)
  })
}

/**
 * Applies the filters. Each facet is OR within itself and AND across facets:
 * picking Michelin and Winter means "Michelin winter tyres", while picking
 * Michelin and Continental means either brand.
 *
 * An offer missing the attribute being filtered on is excluded, not included —
 * a tyre with no season recorded must not surface under "Winter".
 */
export function filterOffers(offers: TyreOffer[], filters: TyreOfferFilters): TyreOffer[] {
  return offers.filter((offer) => {
    if (filters.brands.length > 0 && !filters.brands.includes(offer.brand)) return false
    if (filters.tiers.length > 0 && !(offer.tier && filters.tiers.includes(offer.tier))) return false
    if (filters.seasons.length > 0 && !(offer.season && filters.seasons.includes(offer.season))) {
      return false
    }
    return true
  })
}

export interface FacetOption<T> {
  value: T
  label: string
  count: number
}

/**
 * The facets worth offering, derived from what is actually in the results.
 *
 * Never shows a filter that would return nothing, and never shows a facet with
 * only one option — "filter by brand: Michelin" is not a choice.
 */
export function buildFacets(offers: TyreOffer[]): {
  brands: FacetOption<string>[]
  tiers: FacetOption<TyreTier>[]
  seasons: FacetOption<TyreSeason>[]
} {
  const brandCounts = new Map<string, number>()
  const tierCounts = new Map<TyreTier, number>()
  const seasonCounts = new Map<TyreSeason, number>()

  for (const offer of offers) {
    brandCounts.set(offer.brand, (brandCounts.get(offer.brand) ?? 0) + 1)
    if (offer.tier) tierCounts.set(offer.tier, (tierCounts.get(offer.tier) ?? 0) + 1)
    if (offer.season) seasonCounts.set(offer.season, (seasonCounts.get(offer.season) ?? 0) + 1)
  }

  const brands = [...brandCounts.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([value, count]) => ({ value, label: value, count }))

  const tiers = TIER_ORDER.filter((tier) => tierCounts.has(tier)).map((tier) => ({
    value: tier,
    label: TIER_LABELS[tier],
    count: tierCounts.get(tier) ?? 0,
  }))

  const seasons = SEASON_ORDER.filter((season) => seasonCounts.has(season)).map((season) => ({
    value: season,
    label: SEASON_LABELS[season],
    count: seasonCounts.get(season) ?? 0,
  }))

  return {
    brands: brands.length > 1 ? brands : [],
    tiers: tiers.length > 1 ? tiers : [],
    seasons: seasons.length > 1 ? seasons : [],
  }
}

/** Toggles a value in a filter facet. */
export function toggleFilter<T>(current: T[], value: T): T[] {
  return current.includes(value) ? current.filter((entry) => entry !== value) : [...current, value]
}

export function hasActiveFilters(filters: TyreOfferFilters): boolean {
  return activeFilterCount(filters) > 0
}

/** How many individual values are selected, for the badge on the filter button. */
export function activeFilterCount(filters: TyreOfferFilters): number {
  return filters.brands.length + filters.tiers.length + filters.seasons.length
}

export interface ActiveFilterChip {
  facet: keyof TyreOfferFilters
  value: string
  label: string
}

/**
 * The applied filters, for display outside the panel.
 *
 * A collapsed dropdown hides what has been chosen, which is how customers end
 * up staring at three results and wondering where everything went. These chips
 * keep the answer on screen.
 */
export function describeActiveFilters(filters: TyreOfferFilters): ActiveFilterChip[] {
  return [
    ...filters.tiers.map((value) => ({
      facet: 'tiers' as const,
      value,
      label: TIER_LABELS[value],
    })),
    ...filters.seasons.map((value) => ({
      facet: 'seasons' as const,
      value,
      label: SEASON_LABELS[value],
    })),
    ...filters.brands.map((value) => ({ facet: 'brands' as const, value, label: value })),
  ]
}

/** Removes one value from whichever facet holds it. */
export function removeFilter(
  filters: TyreOfferFilters,
  facet: keyof TyreOfferFilters,
  value: string
): TyreOfferFilters {
  return {
    ...filters,
    [facet]: (filters[facet] as string[]).filter((entry) => entry !== value),
  } as TyreOfferFilters
}

/**
 * Splits a staggered answer into its axles. Offers without an axle (one size
 * all round, or an older backend) are all front.
 */
export function splitOffersByAxle(offers: TyreOffer[]): { front: TyreOffer[]; rear: TyreOffer[] } {
  return {
    front: offers.filter((offer) => offer.axle !== 'REAR'),
    rear: offers.filter((offer) => offer.axle === 'REAR'),
  }
}

/**
 * The chosen tyre as the offers fetched again price it (ACA-98), or null when
 * it is no longer offered, so the customer chooses again.
 */
export function refreshedOffer(previous: TyreOffer | null, offers: TyreOffer[]): TyreOffer | null {
  if (!previous) return null
  return offers.find((offer) => offer.id === previous.id) ?? null
}

/**
 * What `quantity` of a tyre costs, fitted. The server's total when it priced
 * this quantity (ACA-98), which is exactly what the booking charges; otherwise
 * the per-tyre price times the quantity, as older backends expect.
 */
export function offerTotal(offer: TyreOffer, quantity: number): number {
  if (offer.totalPrice != null && offer.quantity === quantity) return offer.totalPrice
  return Math.round(offer.fullyFittedPrice * quantity * 100) / 100
}

/** The note the garage receives, so the order is unambiguous on the workshop sheet. */
export function describeOffer(offer: TyreOffer, quantity: number): string {
  const parts = [`${quantity} × ${offer.brand} ${offer.model} ${offer.size}`.trim()]

  const spec = [offer.loadIndex, offer.speedRating].filter(Boolean).join('')
  if (spec) parts.push(spec)
  if (offer.runFlat) parts.push('run-flat')
  if (offer.extraLoad) parts.push('XL')

  return parts.join(' · ')
}
